/**
 * ContactPicker
 * ─────────────
 * 创货表单专用：从地址簿搜索并选中收货人
 * 支持：搜索框模糊查询 / 显示默认联系人 / 清空已选
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ContactService, SenderContact } from '../../../services/contact.service';

// ─── Types ────────────────────────────────────────────────────
export interface SelectedReceiver {
    name: string;
    phone: string;
    address: string;
    city: string;
    contactId?: string;
}

interface ContactPickerProps {
    value: SelectedReceiver | null;
    onChange: (v: SelectedReceiver | null) => void;
}

// ─── Component ────────────────────────────────────────────────
const ContactPicker: React.FC<ContactPickerProps> = ({ value, onChange }) => {
    const [contacts, setContacts] = useState<SenderContact[]>([]);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    // Load contacts once
    useEffect(() => {
        setLoading(true);
        ContactService.list()
            .then(setContacts)
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    // Auto-fill default on first load (if nothing selected)
    useEffect(() => {
        if (contacts.length === 0 || value !== null) return;
        const def = contacts.find(c => c.is_default);
        if (def) {
            onChange({
                name: def.name,
                phone: def.phone || '',
                address: def.address || '',
                city: def.city || '',
                contactId: def.id,
            });
        }
    }, [contacts]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Focus search on open
    useEffect(() => {
        if (open) setTimeout(() => searchRef.current?.focus(), 80);
    }, [open]);

    const filtered = contacts.filter(c => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            c.name.toLowerCase().includes(q) ||
            c.phone?.includes(q) ||
            c.address?.toLowerCase().includes(q) ||
            c.city?.toLowerCase().includes(q)
        );
    });

    const select = useCallback((c: SenderContact) => {
        onChange({
            name: c.name,
            phone: c.phone || '',
            address: c.address || '',
            city: c.city || '',
            contactId: c.id,
        });
        setOpen(false);
        setSearch('');
    }, [onChange]);

    const clear = () => { onChange(null); setSearch(''); };

    // ── No contacts at all ──────────────────────────────────────
    if (!loading && contacts.length === 0) {
        return (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 text-sm text-gray-500">
                <span className="material-icons text-gray-400 text-base">contacts</span>
                <span>地址簿为空，请前往设置添加收货人</span>
            </div>
        );
    }

    return (
        <div className="relative" ref={panelRef}>
            {/* ── Trigger / Display ── */}
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-all border shadow-sm
                    ${open
                        ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-2 ring-primary/30'
                        : 'bg-white dark:bg-surface-dark border-slate-200 dark:border-white/10 hover:border-primary/50'
                    }
                `}
            >
                {/* Icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${value ? 'bg-cyan-500/15 text-cyan-400' : 'bg-gray-100 dark:bg-white/5 text-gray-400'
                    }`}>
                    <span className="material-icons text-base">
                        {value ? 'person_pin' : 'person_add'}
                    </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {value ? (
                        <>
                            <div className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                                {value.name}
                                {value.phone && (
                                    <span className="text-[11px] font-mono text-gray-500 font-normal">
                                        {value.phone}
                                    </span>
                                )}
                            </div>
                            {(value.city || value.address) && (
                                <div className="text-[11px] text-gray-500 truncate mt-0.5">
                                    {[value.city, value.address].filter(Boolean).join(' · ')}
                                </div>
                            )}
                        </>
                    ) : (
                        <span className="text-sm text-gray-400">点击选择收货人...</span>
                    )}
                </div>

                {/* Right icons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                    {value && (
                        <span
                            role="button"
                            onClick={e => { e.stopPropagation(); clear(); }}
                            className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                        >
                            <span className="material-icons text-[16px]">close</span>
                        </span>
                    )}
                    <span className={`material-icons text-gray-400 text-base transition-transform ${open ? 'rotate-180' : ''}`}>
                        expand_more
                    </span>
                </div>
            </button>

            {/* ── Dropdown Panel ── */}
            {open && (
                <div className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-white dark:bg-[#0f1e35] border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden"
                    style={{ maxHeight: '55vh' }}>

                    {/* Search */}
                    <div className="p-3 border-b border-slate-100 dark:border-white/5">
                        <div className="flex items-center gap-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2">
                            <span className="material-icons text-gray-400 text-base">search</span>
                            <input
                                ref={searchRef}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="搜索姓名、电话、地址..."
                                className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 outline-none"
                            />
                            {search && (
                                <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                                    <span className="material-icons text-base">close</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto no-scrollbar" style={{ maxHeight: 'calc(55vh - 64px)' }}>
                        {loading ? (
                            <div className="flex justify-center items-center py-6 text-gray-500">
                                <span className="material-icons text-2xl animate-spin">refresh</span>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="py-8 text-center text-sm text-gray-500">
                                没有匹配的联系人
                            </div>
                        ) : (
                            filtered.map(c => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => select(c)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 dark:border-white/3 last:border-none transition-colors
                                        ${value?.contactId === c.id
                                            ? 'bg-cyan-50 dark:bg-cyan-500/10'
                                            : 'hover:bg-slate-50 dark:hover:bg-white/5'
                                        }
                                    `}
                                >
                                    {/* Avatar */}
                                    <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${c.is_default
                                            ? 'bg-primary/15 text-primary'
                                            : 'bg-gray-100 dark:bg-white/8 text-gray-500 dark:text-gray-400'
                                        }`}>
                                        {c.name.charAt(0)}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-900 dark:text-white">{c.name}</span>
                                            {c.is_default && (
                                                <span className="text-[10px] px-1 py-px rounded bg-primary/15 text-primary font-bold border border-primary/20">
                                                    默认
                                                </span>
                                            )}
                                            {c.tag && (
                                                <span className="text-[10px] px-1 py-px rounded bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10">
                                                    {c.tag}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                                            {c.phone && <span className="font-mono">{c.phone}</span>}
                                            {c.city && <span>{c.city}</span>}
                                        </div>
                                        {c.address && (
                                            <div className="text-[10px] text-gray-400 truncate mt-0.5">{c.address}</div>
                                        )}
                                    </div>

                                    {/* Selected check */}
                                    {value?.contactId === c.id && (
                                        <span className="material-icons text-cyan-500 text-base flex-shrink-0">check_circle</span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactPicker;
